import React, { useState, useCallback } from 'react';
import { FaEnvelope, FaKey, FaArrowLeft, FaPlane, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { forgotPassword } from '../api/client';
import SimpleCaptcha from './SimpleCaptcha';

const ForgotPassword = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!captchaToken || !captchaAnswer) {
      newErrors.captcha = 'Please solve the math problem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCaptchaVerify = useCallback((token, answer) => {
    setCaptchaToken(token);
    setCaptchaAnswer(answer);
    // Clear captcha error when user provides an answer
    if (token && answer) {
      setErrors(prev => prev.captcha ? { ...prev, captcha: '' } : prev);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await forgotPassword(email, captchaToken, captchaAnswer);
      setSubmitted(true);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to process request. Please try again.';
      setErrorMessage(message);
      // Reset CAPTCHA on error
      setCaptchaKey(prev => prev + 1);
      setCaptchaToken('');
      setCaptchaAnswer('');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="w-full max-w-md animate-scale-in">
        <div className="bg-white rounded-2xl shadow-elevated p-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaCheckCircle className="text-4xl text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-800 mb-4">Check Your Email</h1>
            <p className="text-neutral-600 mb-6">
              If an account exists with <span className="font-semibold text-primary-600">{email}</span>,
              we've sent a temporary password to your inbox.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-amber-800 text-sm">
                <strong>Important:</strong> You'll be required to change your password when you log in with the temporary password.
              </p>
            </div>
            <button
              onClick={onBackToLogin}
              className="w-full bg-gradient-primary text-white py-3 px-6 rounded-lg font-semibold hover:shadow-elevated hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
            >
              <FaArrowLeft />
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md animate-scale-in">
      <div className="bg-white rounded-2xl shadow-elevated p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center gap-2 mb-4">
            <FaKey className="text-4xl text-primary-500" />
            <FaPlane className="text-3xl text-secondary-400" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-500 to-secondary-400 bg-clip-text text-transparent mb-2">
            Forgot Password
          </h1>
          <p className="text-neutral-600">Enter your email to receive a temporary password</p>
        </div>

        {/* Error Banner */}
        {errorMessage && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-6 animate-slide-down flex items-start gap-3">
            <FaExclamationCircle className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm">{errorMessage}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Email Field */}
          <div>
            <label htmlFor="email" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
              <FaEnvelope className="text-primary-500" />
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all ${
                errors.email
                  ? 'border-red-500 bg-red-50'
                  : 'border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100'
              } outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
              disabled={loading}
            />
            {errors.email && (
              <p className="text-red-600 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* CAPTCHA */}
          <div>
            <SimpleCaptcha
              key={captchaKey}
              onVerify={handleCaptchaVerify}
              disabled={loading}
            />
            {errors.captcha && (
              <p className="text-red-600 text-sm mt-2">{errors.captcha}</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-primary text-white py-3 px-6 rounded-lg font-semibold text-lg shadow-travel hover:shadow-elevated hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Sending...
              </span>
            ) : (
              'Send Temporary Password'
            )}
          </button>
        </form>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <button
            onClick={onBackToLogin}
            disabled={loading}
            className="text-primary-600 font-semibold hover:text-primary-700 hover:underline transition-colors inline-flex items-center gap-1 disabled:opacity-50"
          >
            <FaArrowLeft />
            Back to Login
          </button>
        </div>
      </div>

      {/* Decorative Quote */}
      <p className="text-center text-white/80 mt-6 italic text-sm">
        "Every setback is a setup for a comeback."
      </p>
    </div>
  );
};

export default ForgotPassword;
